import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pencil, ScrollText } from 'lucide-react';
import { outreachApi } from '../../api/client';
import { useTranslation } from '../../i18n';
import StoryCard from './StoryCard';
import StoryWizard from './StoryWizard';
import './MyStory.css';

// The "My Stories" tab of the Stories screen: the list of a user's own stories.
// Self-contained (the Stories screen provides the page chrome + tab bar) — own
// list, FAB → wizard, and read/edit navigation. Tapping a finished story opens
// its read view; a draft (no story text yet) opens the editor directly. A
// `?create=1` query param (from the profile empty CTA) auto-opens the wizard.
export default function MyStoriesPanel() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation('testimony');
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const reload = useCallback(() => outreachApi.listStories()
    .then(d => setStories(d.stories || []))
    .catch(() => setError(t('loadError'))), [t]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    outreachApi.listStories()
      .then(d => { if (alive) setStories(d.stories || []); })
      .catch(() => { if (alive) setError(t('loadError')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [t]);

  // Arriving with create intent (e.g. the empty CTA on a profile) opens the wizard.
  useEffect(() => {
    if (params.get('create')) {
      setCreating(true);
      setParams({ tab: 'mine' }, { replace: true });
    }
  }, [params, setParams]);

  function openStory(story) {
    if (story.combinedHtml) navigate(`/testimony/${story.id}`);
    else setEditingId(story.id);
  }

  function closeModal() { setCreating(false); setEditingId(null); }

  if (loading) {
    return <div className="ms-panel"><span className="spinner" style={{ width: 32, height: 32 }} /></div>;
  }

  return (
    <div className="ms-panel">
      {error && <div className="ms-error">{error}</div>}

      {stories.length === 0 ? (
        <div className="empty-state ms-empty">
          <ScrollText size={40} strokeWidth={1.4} color="var(--text-lt)" />
          <h3>{t('empty')}</h3>
          <p>{t('emptyHint')}</p>
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Pencil size={16} /> {t('emptyCta')}
          </button>
        </div>
      ) : (
        <div className="ms-grid">
          {stories.map(s => (
            <StoryCard key={s.id} story={s} onOpen={() => openStory(s)} />
          ))}
        </div>
      )}

      <button className="fab" onClick={() => setCreating(true)} aria-label={t('newStory')}>
        <Pencil size={20} strokeWidth={2} />
      </button>

      {(creating || editingId) && (
        <StoryWizard
          storyId={editingId}
          onClose={() => { closeModal(); reload(); }}
          onSaved={() => { closeModal(); reload(); }}
          onDeleted={() => { closeModal(); reload(); }}
        />
      )}
    </div>
  );
}
